import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
  CountryHub,
  HiddenInput,
  SignInComponentFieldsInfo,
  SignInEvent,
} from '@frk/eds-components';
import {
  isExternalLink,
  openInNewTab,
  setMenuIcon,
} from '@marketing/marketing-utils';
import { AbstractBaseComponent } from '@shared/abstract-base/abstract-base.component';
import {
  CommonConfig,
  Segment,
  SegmentId,
  SimplyLinkItem,
  SiteParams,
} from '@types';
import { Logger } from '@utils/logger';
import find from 'lodash/find';
import get from 'lodash/get';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// Interfaces
import { GlobalItem, SignInNavItems, SiteNavItem } from './header.interfaces';
import {
  AnalyticsLogin,
  SignInInterface,
} from '@shared/sign-in/sign-in.interface';

// Services
import { ANGULAR_HOOK, MYFUNDS_HOOK } from '@utils/app.constants';
import { AppStateService } from '@services/app-state.service';
import { CartHandlerService } from '@literature/services/cart-handler.service';
import { GlobalConfigService } from '@services/global-config-service';
import { IUserProfile, LoginSource } from '@services/profile.interface';
import { LanguageSelectorService } from '@services/language-selector.service';
import { ProfileService } from '@services/profile.service';
import { RoleSelectorService } from '@marketing/services/role-selector.service';
import { SegmentService } from '@services/segment.service';
import { SignInService } from '@services/sign-in.service';
import { TranslateService } from '@shared/translate/translate.service';

const logger = Logger.getLogger('HeaderComponent');

/**
 * Header Component
 */
@Component({
  selector: 'ft-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent
  extends AbstractBaseComponent
  implements OnInit, OnDestroy {
  private unsubscribe$: Subject<void> = new Subject<void>();
  public isSubmitButtonDisabled = false;

  public globalItems: GlobalItem[] = [];

  /**
   * Site config
   */
  siteParams: SiteParams;
  siteGeneral: CommonConfig;

  /**
   * Header Logo
   */
  headerLogo = '/assets/images/logos/FT_logo_pos_RGB@2x.png';

  /**
   * Header Logo Alt
   */
  logoAltText: string;
  homeUrl: string;
  cartSize = 0;
  isCartEnabled = false;
  siteNavData: SiteNavItem[];

  /**
   * current segment data
   */
  hasMoreThanOneVisibleSegment: boolean;
  currentSegment: Segment;
  currentSegmentLabel: string;

  /**
   * Header microsite Logo
   */
  isMicrosite: boolean;
  hasTextLogo: boolean;
  micrositeTextLogo = '';
  micrositeLogoHref = '';

  // Sign In
  hasSignInBtn = false;
  isLogIn = false;
  private profile: IUserProfile;
  signInForm: FormGroup;
  screenReaderSignInButtonText: string;
  screenReaderSignOutButtonText: string;
  signInNavItems: SignInNavItems[] = [];
  signInComponentContent: SignInComponentFieldsInfo;
  signInText: string;
  signOutText: string;
  signInFormAction = '';
  showLegacySignInOption: boolean;
  showLegacyContent = true;
  screenReaderNavToggleBtn: string;
  signInHiddenInputs: HiddenInput[];
  /**
   * TODO Flag and params deciding if search needs to be displayed
   * hasSearch will be read from config.
   * TBD
   */
  // Search
  hasSearch = false;
  // searchLabel = 'Search';
  searchLabel: string;
  searchPlaceholder = 'Search Franklin Templeton';
  searchClearText = 'Clear';
  searchFieldId = 'site-search';

  // Header Style
  headerStyle: 'basic' | 'standard';
  countryHub: CountryHub;
  showRoleSelectionLink: boolean;

  langItems: SimplyLinkItem[] = [];

  /**
   * Constructor
   */
  constructor(
    private appStateService: AppStateService,
    private pageConfigService: GlobalConfigService,
    private cartService: CartHandlerService,
    private changeDetection: ChangeDetectorRef,
    private profileService: ProfileService,
    private segmentService: SegmentService,
    private signInService: SignInService,
    private formBuilder: FormBuilder,
    private translateService: TranslateService,
    private languageSelectorService: LanguageSelectorService,
    private roleSelectorService: RoleSelectorService
  ) {
    super();
    this.siteParams = this.pageConfigService.getSiteParams();
    this.siteGeneral = this.pageConfigService.getSiteGeneral();
    this.headerLogo = get(
      this.siteParams,
      'headerLogo',
      '/assets/images/logos/FT_logo_pos_RGB@2x.png'
    );
    this.logoAltText = get(
      this.siteParams,
      'logoAltText',
      'Franklin Templeton Investments'
    );
    this.homeUrl = this.relativeHomeURL(this.appStateService.getHomePageUrl());
    this.isMicrosite = !!this.siteParams?.isMicrosite;
    this.hasTextLogo =
      typeof this.siteParams?.headerMicrositeTextLogo !== 'undefined' &&
      this.siteParams?.headerMicrositeTextLogo !== '';
    if (this.isMicrosite && this.hasTextLogo) {
      this.micrositeTextLogo = this.siteParams?.headerMicrositeTextLogo;
      this.micrositeLogoHref =
        typeof this.siteParams?.headerMicrositeLogoHref !== 'undefined' &&
        this.siteParams?.headerMicrositeLogoHref !== ''
          ? this.siteParams?.headerMicrositeLogoHref
          : this.homeUrl;
    }
    this.hasSearch = this.siteGeneral?.searchHeader || false;
    this.showLegacySignInOption =
      this.siteGeneral?.authenticationLegacy || false;
    this.hasSignInBtn = this.appStateService.hasAuthentication();
    if (this.hasSignInBtn) {
      this.signInForm = this.signInService.signInFormInit(this.formBuilder);
    }
    // Initiate default signInComponentContent object.
    this.signInComponentContent = this.signInService.setDefaultComponent();
    if (this.signInComponentContent) {
      this.profileService
        .isAccountsAvailable$()
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((message) => {
          this.signInComponentContent.errorText = message;
        });
    }
  }

  /**
   * Init
   */
  ngOnInit(): void {
    this.screenReaderSignInButtonText = this.translateService.instant(
      'signin.signIn'
    );
    this.screenReaderSignOutButtonText = this.translateService.instant(
      'signin.signOutMobile'
    );
    this.searchLabel = this.translateService.instant('signin.search');
    this.screenReaderNavToggleBtn = this.translateService.instant(
      'signin.screenReaderNavToggleBtn'
    );
    this.labelIcon = this.page.isPreview() ? '[L]' : '';
    logger.debug('Header initialised');
    this.cartService
      .getCartCount()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((count) => {
        this.cartSize = count;
        this.isCartEnabled = true;
        this.changeDetection.detectChanges();
      });
    if (this.hasSignInBtn) {
      // Load SignIn forms only if sign in is enabled.
      this.loadSignInContentFromLabels();
    }
    this.initHeaderStyle();

    // role selector link in a header
    this.showRoleSelectionLink = !this.headerStyle?.startsWith('basic');
    this.hasMoreThanOneVisibleSegment =
      this.segmentService.getVisibleSegments()?.length > 1;

    this.currentSegment = this.segmentService.getCurrentSegment();
    this.currentSegmentLabel =
      this.hasMoreThanOneVisibleSegment && this.showRoleSelectionLink
        ? this.currentSegment.label
        : '';

    // WDE-292 this line turns of functionality as this was not fully approved for current release.
    this.currentSegmentLabel = '';

    if (this.appStateService.getAuthenticationType() === 'myfunds') {
      this.signInFormAction = this.signInService.getLoginUrl();
      this.signInHiddenInputs = [
        // TODO load values below dynamically.
        { name: 'locale', value: this.appStateService.getMyFundsLocale() },
        {
          name: 'segment',
          value:
            this.segmentService.getCurrentSegmentId() === SegmentId.INVESTOR
              ? 'INVESTOR'
              : 'ADVISOR',
        },
      ];
    }
    // this delays processing until validated user profile has returned
    this.profileService
      .getUserProfile$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((profile: IUserProfile): void => {
        logger.debug('profile set in header', profile);
        this.profile = profile;

        // Due to Mega-menu functionality getSiteNavData needs to be initialised once in OnInit
        this.siteNavData = this.getSiteNavData();
        logger.debug('processed siteNavData', this.siteNavData);

        // Show/hide login
        if (this.profile.isLoggedIn && this.hasSignInBtn) {
          this.isLogIn = this.profile.isLoggedIn;
          if (
            this.appStateService.getAuthenticationType() !== 'myfunds' &&
            // If bypass parameter does not exist in URL and user is still bypassed we are not able to set these values.
            !this.profileService.isBypass(true)
          ) {
            this.signInNavItems[0].href = this.profile.profileInfo?.dashboardUrl;
            this.signInNavItems[1].href =
              this.appStateService.getProfileDomain() + '/profile/webprofile';
            this.signInNavItems[1].text = this.profile.profileInfo?.displayName;
          }
        }
        // Replacing home URL with partner dashboard URL if configured.
        if (this.profile.profileInfo?.firm) {
          this.homeUrl =
            this.profileService.getPartnerDashboardUrl(
              this.profile.profileInfo?.firm
            ) || this.homeUrl;
        }

        this.changeDetection.detectChanges();
      });

    /**
     * Get language selector for footer
     */
    this.langItems = this.languageSelectorService.getConvertedLanguages();

    /**
     * Set country hub for gateway
     */
    const channelParams = this.page.getChannelParameters();
    this.countryHub = {
      countryHubLink: this.siteParams?.countryHubLink,
      siteName:
        channelParams && channelParams['Site Name']
          ? channelParams['Site Name']
          : '',
    };
  }

  /**
   * Note: this isn't run until profileService returns a validated profile
   */
  private initHeaderStyle() {
    const pageMetadata = this.page.getComponent()?.getModels()?.pageData;

    // Header style
    this.headerStyle = pageMetadata?.headerStyle;
    if (this.hasSignInBtn) {
      this.signInService.setComponentParams(
        this.setSignInStyle(this.headerStyle),
        this.signInComponentContent
      );
    }
  }

  private setSignInStyle(headerStyle): SignInInterface {
    if (headerStyle === 'basic-with-login') {
      return {
        legacy: false,
        signOut: true,
        skin: 'basic',
        showLegacyContent: this.showLegacySignInOption,
        subTitle: ' ',
        signInBodyContent: ' ',
      };
    }
    return {
      legacy: false,
      signOut: true,
      skin: 'basic',
      showLegacyContent: this.showLegacySignInOption,
    };
  }

  private loadSignInContentFromLabels() {
    if (
      this.appStateService.getAuthenticationType() !== 'myfunds' &&
      !this.profileService.isBypass(true)
    ) {
      this.signInNavItems = [
        {
          text: this.translateService.instant('signin.accountDashboard'),
          href: '',
          iconType: this.translateService.instant('signin.lock'),
        },
        {
          text: this.translateService.instant('signin.userName-label'),
          href: '',
          iconType: this.translateService.instant('signin.profile'),
          eventParams: {
            link_text: this.translateService.instant('User Name'),
          },
        },
      ];
    }

    this.signInText = this.translateService.instant('signin.signInRegister');
    this.signOutText = this.translateService.instant('signin.signOut');
  }

  /**
   * Get label for language dropdown
   */
  get languageLabel() {
    const channelLang = this.languageSelectorService.getChannelLanguageLabel(
      this.page
    );
    return this.labelIcon + channelLang;
  }

  /**
   * Get Navigational menu for header
   */
  getSiteNavData(): SiteNavItem[] {
    this.menuItem = this.component.getModels<MenuModels>()?.menu;
    const siteNavItems: MenuItem[] = get(this, 'menuItem.siteMenuItems', []);
    logger.debug('getSiteNavData', siteNavItems);

    /**
     * Get global Links for footer
     */
    const globalItems = find(this.menuItem?.siteMenuItems, {
      properties: { 'dxphst:treehash': 'globalItems' },
    });
    this.globalItems = this.convertGlobal(
      get(globalItems, 'childMenuItems', [])
    );

    return this.convertNavItems(siteNavItems);
  }

  /**
   * Converting BR menu item to EDS GlobalItem interface
   * @param globalItems - BR array to convert
   */
  convertGlobal(globalItems: Array<MenuItem>): GlobalItem[] {
    if (!globalItems) {
      return [];
    }
    const globalItemsEds = [];
    globalItems.forEach((item) => {
      const globalItem: GlobalItem = {
        text: item.name,
        href: get(item, '_links.site.href', ''),
        hrefExternal: isExternalLink(item),
        newWindow: openInNewTab(item),
        isCart: this.isChart(item.name),
        iconType: setMenuIcon(item),
      };
      globalItemsEds.push(globalItem);
    });
    return globalItemsEds;
  }

  /**
   * Check if menu item contains cart
   * @param name - menu item name
   */
  isChart(name: string): boolean {
    return name.toLowerCase().includes('cart');
  }

  /**
   * Converting BR menu item to EDS NavItem interface
   * @param siteNavItems - BR array to convert
   */
  convertNavItems(siteNavItems: Array<MenuItem>): SiteNavItem[] {
    if (!siteNavItems) {
      return [];
    }
    const siteNavItemsEds: SiteNavItem[] = [];
    const mainNavItems: MenuItem = siteNavItems.find(
      (mainNav) => mainNav.properties['dxphst:treehash'] === 'mainNav'
    );
    siteNavItems = mainNavItems?.childMenuItems
      ? mainNavItems.childMenuItems
      : siteNavItems;
    siteNavItems.forEach((item: MenuItem) => {
      if (item.properties['dxphst:treehash'] !== 'globalItems') {
        siteNavItemsEds.push(this.navMenuItems(item));
      }
    });
    return siteNavItemsEds;
  }

  /**
   * Returns object for Nav menu
   * @param item - nav item
   */
  private navMenuItems(item: MenuItem): SiteNavItem {
    let link: string = get(item, '_links.site.href', '');
    const hook: string = this.getHook(item, ANGULAR_HOOK);
    if (hook === MYFUNDS_HOOK) {
      link = this.processMyFunds(link);
    }
    return {
      id: item.name,
      name: item.name,
      active: false,
      link,
      hook,
      hrefExternal: isExternalLink(item),
      newWindow: openInNewTab(item),
      child:
        item.childMenuItems.length > 0
          ? this.convertNavItems(item.childMenuItems)
          : [],
    };
  }

  /**
   * used to extract optional metadata for link
   */
  private getHook(item: MenuItem, paramName: string): string {
    const paramNames: string[] = item.properties?.['hst:parameternames'] || [];
    const paramIndex: number = paramNames.indexOf(paramName);
    if (paramIndex > -1) {
      return item.properties?.['hst:parametervalues'][paramIndex];
    }
  }

  /**
   * Intercepts MyFunds link and redirects to portal if user is logged in
   */
  private processMyFunds(link: string): string {
    let newLink: string = link;
    if (
      this.profile?.isLoggedIn &&
      [LoginSource.OAUTH, LoginSource.DEBUG].includes(this.profile?.loginSource)
    ) {
      switch (this.segmentService.getCurrentSegmentId(true)) {
        case SegmentId.INVESTOR:
          newLink = `${this.appStateService.getMyFundsDomain()}/InternationalInvestorPortal/dashboard`;
          break;
        case SegmentId.FINANCIAL_PROFESSIONALS:
          newLink = `${this.appStateService.getMyFundsDomain()}/InternationalAdvisorPortal/dashboard`;
          break;
        // NOTE: No default case. Only changes url if in specific roles
      }
    }
    return newLink;
  }

  /**
   * Rewrites absolute home page url to relative
   * @param url - string to check
   */
  relativeHomeURL(url: string): string {
    // Logic moved to service.
    return this.appStateService.relativeHomeURL(url);
  }

  /**
   * opens role selector modal
   */
  showRoleSelectorDialog() {
    this.roleSelectorService.openRoleSelectorModal();
  }

  goToCart() {
    this.cartService.navigateToUrl(`yourcart`);
  }

  public onSubmit(signIn: SignInEvent) {
    logger.debug('SignInEvent', signIn);
    this.signInForm.value.userId = signIn.userName;
    this.signInForm.value.password = signIn.password;
    // Commented now according to NGC-8586
    // this.signInForm.value.rememberMe = signIn.rememberMe ? REMEMBER_ME_YES : REMEMBER_ME_NO;
    if (!signIn.userName && !signIn.password) {
      this.signInComponentContent.userName.invalid = true;
      this.signInComponentContent.password.invalid = true;
    } else if (!signIn.userName && signIn.password) {
      this.signInComponentContent.userName.invalid = true;
      this.signInComponentContent.password.invalid = false;
    } else if (signIn.userName && !signIn.password) {
      this.signInComponentContent.userName.invalid = false;
      this.signInComponentContent.password.invalid = true;
    } else {
      this.signInComponentContent.userName.invalid = false;
      this.signInComponentContent.password.invalid = false;
      this.isSubmitButtonDisabled = true;
      const submitObject: AnalyticsLogin = {
        signInForm: this.signInForm,
        analyticsKey: 'Header',
      };
      this.signInService.onSubmit(submitObject);
    }
  }

  public signOut(): void {
    logger.debug('sign out');
    this.signInService.signOut();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
